import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Paper, Card, CardContent,
  CircularProgress, Alert, Grid
} from '@mui/material';
import { motion } from 'framer-motion';
import { getNatalData } from '../services/transitCalendarService';
import { resolveBirthMoment } from '../utils/natalDate';

const ProfileSelection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProfiles = async () => {
      const token = localStorage.getItem('transit-calendar-token');
      if (!token) {
        navigate('/transit-calendar/login');
        return;
      }

      try {
        // Fetch all user data with profiles
        const data = await getNatalData();
        setUserData(data);
        setProfiles(data.profiles || []);
        setLoading(false);
      } catch (err) {
        console.error('Error loading profiles:', err);
        setError('Failed to load profiles. Please try logging in again.');
        setLoading(false);
      }
    };

    loadProfiles();
  }, [navigate]);

  const handleProfileSelect = (profileId) => {
    navigate(`/transit-calendar?profile_id=${profileId}`);
  };

  const handleNatalToTransitSelect = (profileId) => {
    navigate(`/natal-to-transit-calendar?profile_id=${profileId}`);
  };

  const handleNatalTransitSelect = (profileId) => {
    navigate(`/natal-transit-calendar?profile_id=${profileId}`);
  };

  const handleAddProfile = () => {
    navigate('/transit-calendar/add-profile');
  };

  const handleLogout = () => {
    localStorage.removeItem('transit-calendar-token');
    localStorage.removeItem('transit-calendar-access-token');
    localStorage.removeItem('transit-calendar-refresh-token');
    localStorage.removeItem('transit-calendar-user');
    localStorage.removeItem('transit-calendar-email');
    navigate('/transit-calendar/login');
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  const canAddMore = profiles.length < (userData?.max_profiles || 5);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 8
    }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <Box sx={{
            mb: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: 'white',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              Select Profile
            </Typography>
            <Button
              variant="outlined"
              onClick={handleLogout}
              sx={{
                borderColor: 'white',
                color: 'white',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              Log Out
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Welcome Message */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              Welcome, {userData?.name}!
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
            >
              You have {profiles.length} of {userData?.max_profiles || 5} profiles.
              {canAddMore && ' Click a profile to view its transit calendar, or add a new one.'}
              {!canAddMore && ' You have reached the maximum number of profiles for your account.'}
            </Typography>
          </Paper>

          {/* Profile Cards */}
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {profiles.map((profile, index) => (
              <Grid item xs={12} sm={6} lg={4} key={profile.profile_id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: { xs: 'none', sm: 'translateY(-8px)' },
                        boxShadow: { xs: '0 4px 12px rgba(0,0,0,0.1)', sm: '0 12px 24px rgba(0,0,0,0.2)' },
                        borderColor: '#667eea'
                      },
                      border: '2px solid transparent'
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          fontWeight: 700,
                          color: '#667eea',
                          fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}
                      >
                        {profile.profile_name}
                      </Typography>

                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          Born: {(resolveBirthMoment(profile)?.localDate || new Date(profile.birth_date)).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          Time: {profile.birth_time}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          Location: {profile.birth_location}
                        </Typography>
                      </Box>

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProfileSelect(profile.profile_id);
                        }}
                        sx={{
                          mt: { xs: 2, sm: 3 },
                          py: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                          borderColor: '#667eea',
                          color: '#667eea',
                          '&:hover': {
                            borderColor: '#764ba2',
                            bgcolor: 'rgba(102, 126, 234, 0.04)'
                          }
                        }}
                      >
                        Cosmic Personal Events Calendar
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNatalToTransitSelect(profile.profile_id);
                        }}
                        sx={{
                          mt: 1.5,
                          py: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                          borderColor: '#764ba2',
                          color: '#764ba2',
                          '&:hover': {
                            borderColor: '#667eea',
                            bgcolor: 'rgba(118, 75, 162, 0.04)'
                          }
                        }}
                      >
                        Personal Cosmic Activation
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNatalTransitSelect(profile.profile_id);
                        }}
                        sx={{
                          mt: 1.5,
                          py: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                          borderColor: '#32CD32',
                          color: '#32CD32',
                          '&:hover': {
                            borderColor: '#228B22',
                            bgcolor: 'rgba(50, 205, 50, 0.04)'
                          }
                        }}
                      >
                        Personal Transit Calendar
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}

            {/* Add New Profile Card */}
            {canAddMore && (
              <Grid item xs={12} sm={6} lg={4}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: profiles.length * 0.1 }}
                >
                  <Card
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      minHeight: { xs: 180, sm: 250 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #667eea',
                      bgcolor: 'rgba(102, 126, 234, 0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: { xs: 'none', sm: 'translateY(-8px)' },
                        boxShadow: { xs: '0 4px 12px rgba(102, 126, 234, 0.2)', sm: '0 12px 24px rgba(102, 126, 234, 0.2)' },
                        bgcolor: 'rgba(102, 126, 234, 0.1)'
                      },
                      '&:active': {
                        transform: 'scale(0.98)'
                      }
                    }}
                    onClick={handleAddProfile}
                  >
                    <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                      <Typography
                        variant="h1"
                        sx={{
                          color: '#667eea',
                          mb: { xs: 1, sm: 2 },
                          fontSize: { xs: '3rem', sm: '4rem', md: '6rem' }
                        }}
                      >
                        +
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#667eea',
                          mb: 1,
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}
                      >
                        Add New Profile
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Create a transit calendar for another person
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            )}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ProfileSelection;
